<script>
	const util = require('../utilities');
	const _ = require('underscore');
	const pubsub = require('events/pubsub');
	const Vue = require('vue');
	const logger = require('utilities/logger');

	module.exports = {
		props: {
			formName: {
				required: true,
				type: String
			},
			formData: {
				required: true,
				type: Object
			},
			submitTo: {
				required: true,
				type: Function
			},
			customValidator: {
				required: false,
				type: Function
			},
			mustChangeFrom: {
				required: false,
				type: Object
			},
			delta: {
				required: false,
				type: Object
			},
			deltaForceParams: {
				required: false,
				type: Array
			},
			noCloseModal: {
				required: false,
				type: Boolean
			}
		},
		data() {
			return {
				autoFormUnsubs: [],
				lastModalOptions: undefined
			};
		},
		methods: {
			doSubmit: _.debounce(async function () {
				const formName = this.formName;

				// directive has been unbound (probably a slow user double-click just happened
				// and the form this was destined for has been destroyed)
				if (!formName) {
					logger.info('no formName, submitter is unbound, canceling submit');
					return;
				}

				var $el = $(this.$el);
				var modalId = $el.parents('.modal').attr('id');

				if (modalId && !this.noCloseModal) {
					util.closeModals();
				}

				var cb = (err, result) => {
					var evData = {
						err: err,
						data: result,
						formName: formName
					};

					var evType;

					if (!err) {
						evType = 'submit-succeeded';
					} else {
						evType = 'submit-failed';

						if (modalId && !this.noCloseModal && (!err || !err.noServerConnection)) {
							util.openModal('#' + modalId);
						}
					}
					pubsub.publish(evType, evData);
				};

				// Right before we get the data from the model, issue a 'before-submit' event
				// for any code to perform just-in-time changes to the data (such as the student
				// user ID being added to the password form)
				// This is also used to indicated to the user that data is loading
				pubsub.publish('before-submit', {
					formName: formName
				});

				// wait for potential changes from before-submit
				await Vue.nextTick();

				let data = Object.assign({}, this.formData);

				// because before-submit might change a value we have to check allValid here
				// but since before-submit sets the submitInProgress flag in auto-form-row
				// to true, if allValid is false we need to emit submit-failed
				if (!data.allValid) {
					logger.info('rejecting submit of', this.formName, ', form is not valid');

					pubsub.publish('submit-failed', {
						formName: formName
					});

					return;
				}

				for (var key in data) {
					if (data[key] === '__BLANK__') {
						data[key] = null;
					}
				}

				const delta = this.delta;
				if (delta) {
					let data2 = {};
					const force = this.deltaForceParams || [];
					_.each(data, function (v, k) {
						var old = delta[k];
						if (k !== 'allValid' && (JSON.stringify(old) !== JSON.stringify(v) || _.contains(force, k))) {
							data2[k] = v;
						}
					});
					data = data2;
				}

				const customValidator =
					this.customValidator ||
					function () {
						return true;
					};

				if (customValidator()) {
					var maybeAPromise = this.submitTo(data, cb);

					if (maybeAPromise && typeof maybeAPromise.then === 'function') {
						maybeAPromise
							.then(function (result) {
								cb(null, result);
								return result;
							})
							.fail(cb);
					}
				} else {
					pubsub.publish('submit-failed', {
						formName: formName
					});

					if (modalId && !this.noCloseModal) {
						util.openModal('#' + modalId, this.lastModalOptions);
					}
				}
			}, 500),
			onSubmit(event) {
				event.preventDefault();
				this.doSubmit();
			},
			onForFormName() {
				if (this.formName) {
					// edge case, there are forms that have no auto-form-row components inside
					// so start allValid as true for this case, since that's what would happen
					// if they had even just a submit button
					this.$emit(
						'update:formData',
						Object.assign({}, this.formData, {
							allValid: true
						})
					);

					this.autoFormRows = {};

					const readyUnsub = pubsub.subscribe('auto-form-row-ready-' + this.formName, id => {
						this.onAutoFormRowReady(id);
						return true;
					});
					const destroyUnsub = pubsub.subscribe('auto-form-row-destroy-' + this.formName, id => {
						this.onAutoFormRowDestroy(id);
						return true;
					});
					const validityUnsub = pubsub.subscribe('auto-form-row-validity-' + this.formName, (id, valid) => {
						this.onAutoFormRowValidity(id, valid);
						return true;
					});

					this.autoFormUnsubs = [readyUnsub, destroyUnsub, validityUnsub];

					this.trackModalOptions = (modalId, modalOptions) => {
						this.lastModalOptions = modalOptions;
						return true;
					};

					this.removeModalOptionTracker = this.$subscribe('modal-opened', this.trackModalOptions);
				}
			},
			offForFormName() {
				if (this.formName) {
					for (const unsubscribe of this.autoFormUnsubs) {
						unsubscribe();
					}
				}

				if (this.removeModalOptionTracker) {
					this.removeModalOptionTracker();
					this.removeModalOptionTracker = null;
				}
			},
			updateValidity() {
				var allValid = true;

				_.each(this.autoFormRows, function (rowValid, id) {
					if (!rowValid) {
						allValid = false;
					}
				});

				const mustChangeFrom = this.mustChangeFrom;
				let changed = true;
				if (mustChangeFrom) {
					changed = false;
					const form = this.formData;
					_.each(mustChangeFrom, function (v, k) {
						if (k === 'allValid') {
							return;
						}

						if (mustChangeFrom[k] != form[k]) {
							changed = true;
						}
					});
				}

				allValid = allValid && changed;

				this.$emit(
					'update:formData',
					Object.assign({}, this.formData, {
						allValid
					})
				);
			},
			onAutoFormRowValidity: function (id, valid) {
				this.autoFormRows[id] = valid;
				this.updateValidity();
			},
			onAutoFormRowReady: function (id) {
				this.autoFormRows[id] = true;
			},
			onAutoFormRowDestroy: function (id) {
				delete this.autoFormRows[id];
				this.updateValidity();
			}
		},
		watch: {
			formName: {
				handler() {
					if (this.mustChangeFrom) {
						this.updateValidity();
					}
				},
				deep: true
			}
		},
		mounted() {
			this.onForFormName();
		},
		beforeDestroy() {
			this.offForFormName();
		}
	};
</script>
<template>
	<form
		:name="formName"
		@submit="onSubmit">
		<slot></slot>
	</form>
</template>
